import api from './api';
import { IFormularioAjuste, IItemAjuste } from '../interfaces/AjusteInventario';
import type { MotivoAjusteInventario } from '../constants/motivosAjusteInventario';
import { asPdfBlobOrThrow } from '../utils/apiError';
import {
    mapMotivoCodigoFromApi,
    mapMotivoLabelFromApi,
    normalizeMotivoAjuste,
    withMotivoEnPayloadCreate,
} from '../utils/motivosAjusteInventario';

export function detallesEtiquetaDesdeAjuste(data: unknown): Array<{
    codigoArticulo: string;
    descripcionArticulo: string;
    cantidad: number;
}> {
    const root = (data ?? {}) as Record<string, unknown>;
    const nested = root.formulario as Record<string, unknown> | undefined;
    const raw = root.items ?? nested?.items ?? [];
    if (!Array.isArray(raw)) return [];
    const detalles: Array<{ codigoArticulo: string; descripcionArticulo: string; cantidad: number }> = [];
    for (const it of raw) {
        const row = (it ?? {}) as Record<string, unknown>;
        const codigo = String(row.codigoArticulo ?? row.CodigoArticulo ?? '').trim();
        if (!codigo) continue;
        const desc = String(
            row.descripcion ?? row.Descripcion ?? row.descripcionArticulo ?? row.DescripcionArticulo ?? '',
        ).trim();
        const cant = Number(row.cantidad ?? row.Cantidad ?? 0);
        detalles.push({
            codigoArticulo: codigo,
            descripcionArticulo: desc,
            cantidad: Number.isFinite(cant) ? Math.abs(cant) : 0,
        });
    }
    return detalles;
}

const ajusteInventarioService = {
    /**
     * Catálogo de motivos desde API.
     * GET /api/Ajustes/motivos?tipo=positivo|negativo (tipo opcional).
     */
    getMotivosAjuste: async (tipo?: 'positivo' | 'negativo'): Promise<MotivoAjusteInventario[]> => {
        const response = await api.get('/api/Ajustes/motivos', {
            params: tipo ? { tipo } : undefined,
        });
        const data = response.data as unknown;
        const rawList = Array.isArray(data)
            ? data
            : Array.isArray((data as { items?: unknown })?.items)
              ? (data as { items: unknown[] }).items
              : [];
        return rawList
            .map((row) => normalizeMotivoAjuste((row ?? {}) as Record<string, unknown>))
            .filter((m): m is MotivoAjusteInventario => m != null);
    },

    // Obtener el siguiente número de formulario
    getNextNroFormulario: async (tipo: string): Promise<{
        nroFormulario: number;
        letra?: string | null;
        nroComprobante?: number | null;
        nroSuc?: number | null;
    }> => {
        const response = await api.get('/api/ajustes/nextNumber', { params: { tipo } });
        const data = response.data as unknown;
        if (typeof data === 'number' && Number.isFinite(data)) {
            return { nroFormulario: data };
        }
        if (typeof data === 'string' && data.trim() !== '' && Number.isFinite(Number(data))) {
            return { nroFormulario: Number(data) };
        }
        const o = (data ?? {}) as Record<string, unknown>;
        const nested = ((o.formulario ?? o.data ?? o) as Record<string, unknown>) || {};
        const nroFormulario = Number(
            nested.nroFormulario ?? nested.NroFormulario ?? nested.nextNumber ?? nested.numero ?? o.nroFormulario ?? 0
        );
        const letraRaw = nested.letra ?? nested.Letra ?? o.letra;
        const nroComprobante = Number(nested.nroComprobante ?? nested.NroComprobante ?? o.nroComprobante);
        const nroSuc = Number(nested.nroSuc ?? nested.NroSuc ?? o.nroSuc);
        return {
            nroFormulario: Number.isFinite(nroFormulario) ? nroFormulario : 0,
            letra: letraRaw != null && String(letraRaw).trim() !== '' ? String(letraRaw).trim() : null,
            nroComprobante: Number.isFinite(nroComprobante) && nroComprobante > 0 ? nroComprobante : null,
            nroSuc: Number.isFinite(nroSuc) && nroSuc > 0 ? nroSuc : null,
        };
    },

    // Crear un nuevo ajuste de inventario
    createAjuste: async (formulario: IFormularioAjuste, items: IItemAjuste[], tipo: string) => {
        // Preparar el payload según el nuevo formato del endpoint POST /api/Ajustes
        const fecha = formulario.fechaEmision instanceof Date 
            ? formulario.fechaEmision.toISOString() 
            : (typeof formulario.fechaEmision === 'string' 
                ? formulario.fechaEmision 
                : new Date().toISOString());

        const payloadBase = {
            fecha: fecha,
            nroSuc: formulario.nroSuc || 0,
            descripcion: formulario.descripcion || '',
            nroUbicacionOrigen: formulario.nroUbicacionOrigen || 0,
            nroUbicacionDestino: formulario.nroUbicacionDestino || 0,
            items: items.map(item => ({
                codigoArticulo: item.codigoArticulo || '',
                descripcion: item.descripcion || '',
                cantidad: Number(item.cantidad) || 0,
                costo: Number(item.costo) || 0,
                notas: item.notas || '',
                lotes: item.lotes ?? [],
            }))
        };
        const payload = withMotivoEnPayloadCreate(payloadBase, formulario.motivoCodigo, tipo);

        console.log('📤📤📤 POST /api/Ajustes - PAYLOAD COMPLETO:', JSON.stringify(payload, null, 2));
        console.log('📤 POST /api/Ajustes - Parámetro tipo:', tipo);
        console.log('📤 POST /api/Ajustes - Verificación de ubicaciones:', {
            nroUbicacionOrigen: payload.nroUbicacionOrigen,
            nroUbicacionDestino: payload.nroUbicacionDestino,
            tipoAjuste: tipo,
            esNegativo: tipo === 'negativo' && payload.nroUbicacionDestino === 0
        });
        
        // VERIFICACIÓN CRÍTICA antes de enviar
        if (tipo === 'negativo' && payload.nroUbicacionDestino !== 0) {
            console.error('❌❌❌ ERROR CRÍTICO: Intentando guardar ajuste negativo con nroUbicacionDestino != 0');
            console.error('❌ El backend puede guardarlo incorrectamente como transferencia');
        }
        
        const response = await api.post('/api/Ajustes', payload);
        console.log('📥 POST /api/Ajustes - Respuesta:', response.data);
        console.log('📥 POST /api/Ajustes - Respuesta completa:', JSON.stringify(response.data, null, 2));
        return response.data;
    },

    // Actualizar un ajuste de inventario
    updateAjuste: async (nroFormulario: number, formulario: IFormularioAjuste, items: IItemAjuste[], tipo: string) => {
        const formularioConMotivo: IFormularioAjuste = {
            ...formulario,
            motivoCodigo:
                tipo === 'positivo' || tipo === 'negativo'
                    ? formulario.motivoCodigo ?? null
                    : null,
        };
        const response = await api.put(
            `/api/ajustes/${nroFormulario}`,
            { formulario: formularioConMotivo, items },
            { params: { tipo } },
        );
        return response.data;
    },

    // Obtener un ajuste por número
    getAjuste: async (nroFormulario: number) => {
        const response = await api.get(`/api/ajustes/${nroFormulario}`);
        const data = response.data as Record<string, unknown> | null;
        if (!data || typeof data !== 'object') return response.data;
        const formRaw = (data.formulario ?? data) as Record<string, unknown>;
        const motivoCodigo = mapMotivoCodigoFromApi(formRaw) ?? mapMotivoCodigoFromApi(data);
        const motivoLabel = mapMotivoLabelFromApi(formRaw) ?? mapMotivoLabelFromApi(data);
        if (data.formulario && typeof data.formulario === 'object') {
            return {
                ...data,
                formulario: { ...(data.formulario as object), motivoCodigo, motivoLabel },
            };
        }
        return { ...data, motivoCodigo, motivoLabel };
    },

    // IMPORTANTE: El backend puede tener filtros que excluyen ajustes negativos
        // Por eso el ajuste se puede obtener por ID pero no aparece en la lista paginada
        // No agregamos parámetros de ordenamiento porque el backend puede no aceptarlos
        // El problema está en el backend, no en el frontend
    // Obtener lista de ajustes
    getAjustes: async (params: {
        pageNumber: number;
        pageSize: number;
        searchTerm?: string;
        fechaDesde?: string;
        fechaHasta?: string;
        nroSuc?: number;
    }) => {
        const queryParams: Record<string, string | number> = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
        };
        if (params.searchTerm) queryParams.searchTerm = params.searchTerm;
        if (params.fechaDesde) queryParams.fechaDesde = params.fechaDesde;
        if (params.fechaHasta) queryParams.fechaHasta = params.fechaHasta;
        if (params.nroSuc != null && params.nroSuc > 0) queryParams.nroSuc = params.nroSuc;

        console.log('📤 GET /api/ajustes - Parámetros de consulta:', queryParams);
        const response = await api.get('/api/ajustes', { params: queryParams });
        console.log('📥 GET /api/ajustes - Respuesta:', {
            items: response.data.items?.length || 0,
            totalCount: response.data.totalCount,
            pageNumber: response.data.pageNumber,
            pageSize: response.data.pageSize,
            // Verificar si hay ajustes negativos en la respuesta
            ajustesNegativos: response.data.items?.filter((a: any) => 
                a.nroUbicacionOrigen && a.nroUbicacionOrigen > 0 && 
                (!a.nroUbicacionDestino || a.nroUbicacionDestino === 0)
            ).length || 0
        });
        return response.data;
    },

    // Anular un ajuste
    anularAjuste: async (nroFormulario: number) => {
        const response = await api.delete(`/api/ajustes/${nroFormulario}`);
        return response.data;
    },

    // Obtener reporte PDF
    getReporte: async (nroFormulario: number) => {
        const response = await api.get(`/api/ajustes/${nroFormulario}/pdf`, {
            responseType: 'blob'
        });
        return response.data;
    },

    getEtiquetasCodigosPdf: async (
        nroFormulario: number,
        items: Array<{ codigoArticulo: string; copias: number }>,
        idCompania?: string,
    ): Promise<Blob> => {
        const response = await api.post(
            `/api/Ajustes/${nroFormulario}/etiquetas-codigos-pdf`,
            { items },
            { params: { idCompania: idCompania?.trim() || undefined }, responseType: 'blob' },
        );
        return asPdfBlobOrThrow(response.data, 'El servidor no pudo generar las etiquetas');
    },

    getEtiquetasCodigosZpl: async (
        nroFormulario: number,
        items: Array<{ codigoArticulo: string; copias: number }>,
        idCompania?: string,
    ): Promise<string> => {
        const response = await api.post<string>(
            `/api/Ajustes/${nroFormulario}/etiquetas-codigos-zpl`,
            { items },
            { params: { idCompania: idCompania?.trim() || undefined }, responseType: 'text' },
        );
        const zpl = typeof response.data === 'string' ? response.data.trim() : '';
        if (!zpl) throw new Error('El servidor no pudo generar el ZPL de etiquetas');
        return zpl;
    },

    registrarMovStockHistorico: async (data: {
        idformulario: number;
        codigoArticulo: string;
        tipoAjuste: string;
        cantidad: number;
        deposito: number;
        nroSuc: number;
        esEgreso: boolean;
        nroItems: number;
        letra: string;
        nroComprobante: number;
    }) => {
        const response = await api.post('/api/ajustes/registrarMovStockHistorico', data);
        return response.data;
    }
};

export default ajusteInventarioService; 